import _some from 'lodash/some'
import {
  from,
  HttpLink,
  ApolloLink,
} from '@apollo/client'
import { get as getConfig } from 'config'
import _get from 'lodash/get'
import {
  RETRYABLE_STATUS_CODES,
  RETRYABLE_ERROR_CODES,
  API_ERROR_CODE_EADDRINUSE,
  API_ERROR_CODE_EAI_AGAIN,
  API_ERROR_CODE_ECONNREFUSED,
  API_ERROR_CODE_ECONNRESET,
  API_ERROR_CODE_ENETUNREACH,
  API_ERROR_CODE_ENOTFOUND,
  API_ERROR_CODE_EPIPE,
  API_ERROR_CODE_EPROTO,
  API_ERROR_CODE_ETIMEDOUT,
} from 'services/api'
import { post as apiPost, REQUEST_TYPE_APPLICATION_JSON, TYPE_GRAPHQL } from 'api-client'

const config = getConfig()
const graphqlServer = _get(config, ['servers', 'graphql'])

export const APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST = 'cache-first'
export const APOLLO_CLIENT_FETCH_POLICY_CACHE_ONLY = 'cache-only'
export const APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK = 'cache-and-network'
export const APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY = 'network-only'
export const APOLLO_CLIENT_FETCH_POLICY_NO_CACHE = 'no-cache'
export const APOLLO_CLIENT_FETCH_POLICY_STANDBY = 'standby'
export const APOLLO_CLIENT_ERROR_POLICY_ALL = 'all'
export const APOLLO_CLIENT_ERROR_POLICY_NONE = 'none'
export const APOLLO_CLIENT_ERROR_POLICY_IGNORE = 'ignore'

/**
 * The Apollo Client Links
 * @param {Object} options - The options
 * @param {String} options.authJwt - The user's JWT
 * @returns {ApolloLink} - The Apollo Link
 */
export function getApolloClientLinks () {
  const graphqlLink = new HttpLink({
    // Request GraphQL from same domain using origin to prevent cross origin request
    // a proxy should exist /graphql to the GraphQL service for this to work
    uri: `${String(graphqlServer).replace(/\/$/, '')}/graphql`,
    async fetch (_uri, options) {
      /**
       * Use our own API Client to send requests to GraphQL
       * instead of using the browser's built-in "fetch", which wasn't working in Safari
       */
      const response = await apiPost(
        'graphql',
        options.body ? JSON.parse(options.body) : {},
        {
          auth: options?.headers?.authorization ? options.headers.authorization : null,
          reqType: REQUEST_TYPE_APPLICATION_JSON,
        },
        TYPE_GRAPHQL,
      )
      return {
        text: async () => response.text, // Apollo expects a `text()` method
        json: async () => response.body, // Return response as JSON
        ok: response.statusCode >= 200 && response.statusCode < 300,
        status: response.statusCode,
        statusText: response.statusText,
      }
    },
  })

  const authLink = new ApolloLink((operation, forward) => {
    // all queries/mutations will need to send in the auth jwt via context if auth is required
    const { jwt } = operation.getContext()

    if (jwt) {
      operation.setContext(({ headers }) => ({
        headers: {
          ...headers,
          authorization: jwt,
        },
      }))
    }

    return forward(operation)
  })

  const linkChain = from([
    authLink,
    graphqlLink,
  ])

  return linkChain
}

export function isRetryableError (error) {
  const errorCode = _get(error, 'code')
  const statusCode = _get(error, 'statusCode') || _get(error, 'response.statusCode')
  const isRetryableCode = _some([
    statusCode && Number(RETRYABLE_STATUS_CODES.has(statusCode)),
    errorCode && Number(RETRYABLE_STATUS_CODES.has(errorCode)),
    statusCode && Number(RETRYABLE_ERROR_CODES.has(statusCode)),
    errorCode && Number(RETRYABLE_ERROR_CODES.has(errorCode)),
  ])

  // if we can't find a retryable code of any kind, check the error message
  const message = String(_get(error, 'message', '') || '')
  const messageCode = _some([
    message.includes(API_ERROR_CODE_EADDRINUSE),
    message.includes(API_ERROR_CODE_EAI_AGAIN),
    message.includes(API_ERROR_CODE_ECONNREFUSED),
    message.includes(API_ERROR_CODE_ECONNRESET),
    message.includes(API_ERROR_CODE_ENETUNREACH),
    message.includes(API_ERROR_CODE_ENOTFOUND),
    message.includes(API_ERROR_CODE_EPIPE),
    message.includes(API_ERROR_CODE_EPROTO),
    message.includes(API_ERROR_CODE_ETIMEDOUT),
  ])
  const isRetryable = !!isRetryableCode || !!messageCode

  return isRetryable
}
